/**
 * Secondary Navigation Predictive Search Component
 * Handles article search functionality with proper namespacing
 */
class SNPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#sn-predictive-search');
    this.isOpen = false;
    this.abortController = null;

    if (!this.input) return;

    this.input.addEventListener('input', this.debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));

    // Close on click outside
    document.addEventListener('click', this.onClickOutside.bind(this));

    // Close on escape key
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.close();
        this.input.blur();
      }
    });
  }

  onChange(event) {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFocus() {
    const searchTerm = this.input.value.trim();

    if (searchTerm.length && this.predictiveSearchResults.innerHTML.trim()) {
      this.open();
    }
  }

  onClickOutside(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  getSearchResults(searchTerm) {
    // Abort previous request if still pending
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    // Only search for articles - use Section Rendering API
    const url = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(searchTerm)}&resources[type]=article&resources[limit]=5&section_id=sn-predictive-search`;

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-sn-predictive-search');

        if (resultsMarkup) {
          this.predictiveSearchResults.innerHTML = resultsMarkup.innerHTML;
          this.input.setAttribute('aria-expanded', 'true');
          this.open();
        } else {
          this.close();
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          // Request was aborted, ignore
          return;
        }
        this.close();
        console.error('Predictive search error:', error);
      });
  }

  open() {
    if (this.isOpen) return;

    this.predictiveSearchResults.style.display = 'block';
    this.isOpen = true;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.isOpen) return;

    this.predictiveSearchResults.style.display = 'none';
    this.isOpen = false;
    this.input.setAttribute('aria-expanded', 'false');
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('sn-predictive-search', SNPredictiveSearch);
